import React from 'react';
import { toast } from 'sonner';
import { ChevronDown, UserX } from 'lucide-react';
import { adminApi } from '../lib/adminAuth';

const STATUS = { pending: 'bg-amber-100 text-amber-800', in_progress: 'bg-sky-100 text-sky-700', completed: 'bg-emerald-100 text-emerald-700', rejected: 'bg-slate-200 text-slate-600' };
const LABEL = { pending: 'Pending', in_progress: 'In progress', completed: 'Completed', rejected: 'Rejected' };
const when = (v) => (v ? new Date(v).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

function Row({ r, onSaved }) {
  const [status, setStatus] = React.useState(r.status);
  const [note, setNote] = React.useState(r.note || '');
  const save = async () => {
    try { await adminApi.put(`/api/admin/account-deletion-requests/${r._id}`, { status, note }); toast.success('Request updated'); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not update'); }
  };
  const due = new Date(new Date(r.created_at).getTime() + 30 * 86400000);
  return (
    <div className="p-3 rounded-xl border border-slate-200 bg-white space-y-2" data-testid={`deletion-request-${r._id}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <b className="text-slate-800">{r.name}</b><span className="text-slate-500">{r.email}</span>
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS[r.status]}`} data-testid={`deletion-status-${r._id}`}>{LABEL[r.status]}</span>
        <span className="text-xs text-slate-400 ml-auto">{r.reference} · {when(r.created_at)} · respond by {when(due)}</span>
      </div>
      <p className="text-xs text-slate-600">Type: {r.account_type} · Accounts found: {(r.matched_accounts || []).join(', ') || 'none'}{r.reason ? ` · "${r.reason}"` : ''}</p>
      <div className="flex flex-wrap gap-2">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 px-2 rounded-lg border border-slate-200 text-sm bg-white" data-testid={`deletion-status-select-${r._id}`}>
          {Object.entries(LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note (e.g. deleted from careers on 2 Nov)" className="flex-1 min-w-[200px] h-9 px-3 rounded-lg border border-slate-200 text-sm" data-testid={`deletion-note-${r._id}`} />
        <button type="button" onClick={save} className="h-9 px-4 rounded-lg bg-gray-900 text-white text-sm font-semibold" data-testid={`deletion-save-${r._id}`}>Save</button>
      </div>
    </div>
  );
}

export default function DeletionRequestsPanel() {
  const [data, setData] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const load = React.useCallback(() => adminApi.get('/api/admin/account-deletion-requests').then((r) => setData(r.data)).catch(() => setData(null)), []);
  React.useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const pending = (data.counts.pending || 0) + (data.counts.in_progress || 0);
  return (
    <div className="soft-card p-4 mb-6" data-testid="deletion-requests-panel">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 text-left" data-testid="deletion-requests-toggle">
        <UserX className="w-5 h-5 text-rose-500" />
        <span className="flex-1 font-semibold text-[#1a1a2e]">Account deletion requests</span>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${pending ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-500'}`} data-testid="deletion-open-count">{pending} open</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-slate-500">Submitted from girgit.app/delete-account. Confirm the person by email, delete their data by hand, then mark the request completed. The deadline is 30 days.</p>
          {data.requests.length === 0 ? <p className="text-sm text-slate-500 py-2">No requests yet.</p> : data.requests.map((r) => <Row key={r._id} r={r} onSaved={load} />)}
        </div>
      )}
    </div>
  );
}
